/** Shared parsing / grading for MATCH_PAIR answers (sample exam + results UI). */

/** Minimal row shape — avoids importing API types into tiny helpers. */
export type MatchPairDef = {
  pairId?: number;
  leftText?: string;
  rightText?: string;
};

export function normalizeToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

export function parseMatchPairAnswer(raw: unknown): Record<string, string> | null {
  if (raw == null) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, string>;
  }
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Build the expected right-hand value per left label.
 * The student UI always keys answers by trimmed `leftText`. Some APIs key `correctAnswer` by
 * `pairId` instead — merge both so grading matches what we show per row.
 */
export function resolveExpectedPairs(
  correctRaw: unknown,
  matchPairs: MatchPairDef[]
): Record<string, string> | null {
  const parsed = parseMatchPairAnswer(correctRaw) ?? {};
  if (!matchPairs.length) {
    return Object.keys(parsed).length ? parsed : null;
  }
  const out: Record<string, string> = {};
  for (const p of matchPairs) {
    const left = String(p.leftText ?? '').trim();
    if (!left) continue;
    const byLeft = parsed[left];
    const byId = parsed[String(p.pairId ?? '')];
    const fromApi = byLeft ?? byId;
    const canonical = String(p.rightText ?? '').trim();
    const val = String(fromApi ?? '').trim() !== '' ? String(fromApi).trim() : canonical;
    out[left] = val;
  }
  return Object.keys(out).length ? out : null;
}

export function isMatchPairCorrect(
  selected: unknown,
  correctRaw: unknown,
  matchPairs?: MatchPairDef[]
): boolean {
  const selectedMap = parseMatchPairAnswer(selected);
  if (!selectedMap) return false;

  const expected =
    matchPairs && matchPairs.length > 0
      ? resolveExpectedPairs(correctRaw, matchPairs)
      : parseMatchPairAnswer(correctRaw);
  if (!expected || !Object.keys(expected).length) return false;

  const expectedLefts = Object.keys(expected);
  for (const left of expectedLefts) {
    if (normalizeToken(selectedMap[left]) !== normalizeToken(expected[left])) return false;
  }

  const selectedWithValue = Object.keys(selectedMap).filter(
    (k) => String(selectedMap[k] ?? '').trim() !== ''
  );
  if (selectedWithValue.length !== expectedLefts.length) return false;
  const expectedSet = new Set(expectedLefts);
  return selectedWithValue.every((k) => expectedSet.has(k));
}
